"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _SvgChart = _interopRequireDefault(require("../components/SvgChart"));
var d3 = _interopRequireWildcard(require("d3"));
var _useChartData = require("../hooks/useChartData");
var _useChartDimensions = require("../hooks/useChartDimensions");
var _useChartTooltip = require("../hooks/useChartTooltip");
var _ScatterPlot = require("./ScatterPlot.styled");
var _useAxisOverlap = require("../hooks/useAxisOverlap");
var _labelFormatter = require("../utils/label-formatter");
var _constants = require("../constants");
var _chartOperations = require("../utils/chart-operations");
var _useFilter = require("../hooks/useFilter");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
;
const BASE_MARGIN = Object.freeze({
  top: 10,
  bottom: 40,
  left: 50,
  right: 10
});
const AXIS_PADDING = 0.1;
const ScatterPlot = /*#__PURE__*/(0, _react.memo)(chartProps => {
  var _chart$colors;
  const {
    chart,
    animate,
    chartColors,
    theme
  } = chartProps;
  const {
    xCol,
    yCol,
    yFormat,
    xFormat,
    seriesCol
  } = chart;
  const {
    xAxisTitle,
    yAxisTitle,
    showLabels,
    allowZoom
  } = chart.componentProps;
  const palette = ((_chart$colors = chart.colors) === null || _chart$colors === void 0 ? void 0 : _chart$colors.schemes.categorical) || chartColors.schemes.categorical;
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const [animationDone, setAnimationDone] = (0, _react.useState)(!animate);
  const svgRef = _react.default.useRef(null);
  const baseMargin = (0, _react.useMemo)(() => ({
    ...BASE_MARGIN,
    bottom: xAxisTitle ? BASE_MARGIN.bottom + 30 : BASE_MARGIN.bottom,
    left: yAxisTitle ? BASE_MARGIN.left + 30 : BASE_MARGIN.left
  }), [yAxisTitle, xAxisTitle]);
  const {
    detectLabelOverlap,
    labelOverlap
  } = (0, _useAxisOverlap.useAxisOverlap)(svgRef, ".x-axis");
  const {
    width,
    height,
    margin
  } = (0, _useChartDimensions.useChartDimensions)(svgRef, baseMargin, chart.spanRow, ".x-axis text, .y-axis text, .axis-titles text", [yAxisTitle, xAxisTitle]);
  let {
    data,
    yMin,
    yMax,
    xMin,
    xMax
  } = (0, _useChartData.useChartData)(chart, filter);
  const {
    tooltipRef,
    showTooltip,
    hideTooltip
  } = (0, _useChartTooltip.useChartTooltip)(chart, data);
  const xRange = Math.abs(xMax - xMin);
  const xPadding = xRange * AXIS_PADDING;
  const xScale = d3.scaleLinear().domain([xMin < 0 ? xMin - xPadding : Math.max(xMin - xPadding, 0), xMax + xPadding]).range([0, width]);
  const yRange = Math.abs(yMax - yMin);
  const yPadding = yRange * AXIS_PADDING;
  const colorPalette = d3.scaleOrdinal().domain(data.map(d => d.seriesKey)).range(palette);
  const yScale = d3.scaleLinear().domain([yMin < 0 ? yMin - yPadding : Math.max(yMin - yPadding, 0), yMax + yPadding]).range([height, 0]);
  data = d3.sort(data, (a, b) => a.points.filter(p => p._rel === "main").length - b.points.filter(p => p._rel === "main").length);
  const hideOverlapingLabels = (0, _react.useCallback)(() => {
    if (!svgRef.current || !showLabels) {
      return;
    }
    const circles = svgRef.current.querySelectorAll(".scatter-plot circle");
    const labels = svgRef.current.querySelectorAll(".scatter-plot text");
    for (const label of labels) {
      label.style.display = "";
    }
    const boxes = Array.from(circles).map(circle => circle.getBBox()).map((box, idx) => ({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height + 8,
      x2: box.x + box.width,
      y2: box.y + box.height + 8,
      idx
    }));
    const compBoxes = [...boxes];
    let nextIdx = boxes.length;
    for (const box1 of boxes) {
      for (let j = 0; j < compBoxes.length; j++) {
        const box2 = compBoxes[j];
        if (box1.idx === box2.idx) {
          continue;
        }
        if ((0, _chartOperations.boxesIntersect)(box1, box2)) {
          const x = Math.min(box1.x, box2.x);
          const y = Math.min(box1.y, box2.y);
          const x2 = Math.max(box1.x2, box2.x2);
          const y2 = Math.max(box1.y2, box2.y2);
          compBoxes.splice(j, 1);
          compBoxes.splice(compBoxes.findIndex(box => box.idx === box1.idx), 1);
          compBoxes.unshift({
            x,
            y,
            height: y2 - y,
            width: x2 - x,
            x2,
            y2,
            idx: nextIdx++
          });
          break;
        }
      }
    }
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const bbox = label.getBBox();
      const labelBox = {
        x: bbox.x,
        y: bbox.y,
        width: bbox.width,
        height: bbox.height
      };
      for (const box of compBoxes) {
        if ((0, _chartOperations.boxesIntersect)(box, labelBox) && i !== box.idx) {
          label.style.display = "none";
          break;
        }
      }
    }

    // for (const box of intersects) {
    //   d3.select(svgRef.current)
    //   .select('.scatter-plot')
    //   .append("rect")
    //   .attr("x", box.x)
    //   .attr("y", box.y)
    //   .attr("width", box.width)
    //   .attr("height", box.height)
    //   .attr("stroke", "red")
    //   .attr("fill", "none");
    // }
  }, [svgRef.current, data, showLabels]);
  const yAxis = d3.axisLeft(yScale).tickSize(0).tickPadding(8).ticks(8).tickFormat(v => _labelFormatter.QuantitativeFormatter[yFormat](v.toString()));
  const xAxis = d3.axisBottom(xScale).tickSize(labelOverlap ? 5 : 0).tickPadding(labelOverlap ? 2 : 8).tickFormat(v => _labelFormatter.QuantitativeFormatter[xFormat](v.toString()));
  (0, _react.useLayoutEffect)(() => {
    if (svgRef.current === null || height == null) {
      return;
    }
    const mainGroup = d3.select(svgRef.current).select(".main-group");
    mainGroup.select(".x-axis").attr("transform", "translate(0, ".concat(height, ")")).call(xAxis).selectAll(".tick text").style("text-anchor", labelOverlap ? "end" : "middle");
    mainGroup.select(".y-axis").call(yAxis).selectAll(".domain");
    resetZoom();
    detectLabelOverlap();
    hideOverlapingLabels();
    return () => {
      // Remove axis?
    };
  }, [width, height, labelOverlap, chart, filter]);
  (0, _react.useLayoutEffect)(() => {
    if (svgRef.current === null || height == null || !allowZoom) {
      return;
    }
    const mainGroup = d3.select(svgRef.current).select(".main-group");
    const scatter = mainGroup.select(".scatter-plot");
    const bbox = svgRef.current.querySelector(".scatter-plot").getBBox();
    d3.select(svgRef.current).append("defs").append("svg:clipPath").attr("id", "clip-".concat(chart.id)).append("svg:rect").attr("width", bbox.width).attr("height", bbox.height).attr("x", bbox.x).attr("y", bbox.y - 3);
    scatter.attr("clip-path", "url(#clip-".concat(chart.id, ")"));
    if (allowZoom) {
      const brush = d3.brush().extent([[0, 0], [width, height]]).on("end", updateChart);
      scatter.append("g").attr("class", "brush").call(brush);
      const points = data.map(d => d.points).flat();
      function updateChart(event) {
        const extent = event.selection;

        // If no selection, back to initial coordinate. Otherwise, update X axis domain
        if (extent) {
          d3.select(svgRef.current).select("text.reset-zoom").style("display", "block");
          xScale.domain([xScale.invert(extent[0][0]), xScale.invert(extent[1][0])]);
          yScale.domain([yScale.invert(extent[1][1]), yScale.invert(extent[0][1])]);
          brush.clear(mainGroup.select(".brush"));
        } else {
          return;
          //xScale.domain([xMin < 0 ? xMin - xPadding : Math.max(xMin - xPadding, 0), xMax + xPadding]);
        }

        d3.select(svgRef.current).select(".x-axis").transition().duration(1000).call(xAxis);
        d3.select(svgRef.current).select(".y-axis").transition().duration(1000).call(yAxis);
        scatter.selectAll("circle").data(points).transition().duration(1000).attr("cx", function (p) {
          return xScale(Number(p[xCol]));
        }).attr("cy", function (p) {
          return yScale(Number(p[yCol]));
        });
        scatter.selectAll("text").data(points).transition().duration(1000).attr("x", function (p) {
          return xScale(Number(p[xCol]));
        }).attr("y", function (p) {
          return yScale(Number(p[yCol])) + 12;
        });
        scatter.select(".x-zero").transition().duration(1000).attr("x1", xScale(Math.min(xMin, 0))).attr("y1", yScale(0)).attr("x2", xScale(xMax) + 20).attr("y2", yScale(0));
        scatter.select(".y-zero").transition().duration(1000).attr("x1", xScale(0)).attr("y1", yScale(yMin)).attr("x2", xScale(0)).attr("y2", yScale(yMax) - 20);
        setTimeout(hideOverlapingLabels, 1000);
      }
    }
    return () => {
      const svg = d3.select(svgRef.current);
      svg.selectAll("defs").remove();
      svg.select(".main-group").attr("clip-path", undefined);
      if (allowZoom) {
        d3.select(svgRef.current).selectAll(".brush").remove();
        d3.select(svgRef.current).select("text.reset-zoom").style("display", "none");
      }
    };
  }, [allowZoom, yScale, xScale, width, height, labelOverlap, xMin, xMax, xPadding, yMax, yMin, yPadding, chart.id, filter]);
  (0, _react.useLayoutEffect)(() => {
    if (!svgRef.current) {
      return;
    }
    const text = svgRef.current.querySelector(".x-axis-title");
    if (text) {
      const rect = text.getBoundingClientRect();
      text.setAttribute("transform", "translate(-".concat(rect.width / 2, ", 0)"));
    }
  }, [xAxisTitle, yAxisTitle, svgRef.current]);
  const resetZoom = function () {
    xScale.domain([xMin < 0 ? xMin - xPadding : Math.max(xMin - xPadding, 0), xMax + xPadding]);
    yScale.domain([yMin < 0 ? yMin - yPadding : Math.max(yMin - yPadding, 0), yMax + yPadding]);
    const mainGroup = d3.select(svgRef.current).select(".main-group");
    const scatter = mainGroup.select(".scatter-plot");
    const points = data.map(d => d.points).flat();
    scatter.selectAll("circle").data(points).attr("cx", function (p) {
      return xScale(Number(p[xCol]));
    }).attr("cy", function (p) {
      return yScale(Number(p[yCol]));
    });
    scatter.selectAll("text").data(points).attr("x", function (p) {
      return xScale(Number(p[xCol]));
    }).attr("y", function (p) {
      return yScale(Number(p[yCol])) + 12;
    });
    d3.select(svgRef.current).select(".x-axis").call(xAxis);
    d3.select(svgRef.current).select(".y-axis").call(yAxis);
    scatter.select(".x-zero").attr("x1", xScale(Math.min(xMin, 0))).attr("y1", yScale(0)).attr("x2", xScale(xMax) + 20).attr("y2", yScale(0));
    scatter.select(".y-zero").attr("x1", xScale(0)).attr("y1", yScale(yMin)).attr("x2", xScale(0)).attr("y2", yScale(yMax) - 20);
    d3.select(svgRef.current).select("text.reset-zoom").style("display", "none");
    hideOverlapingLabels();
  };
  return /*#__PURE__*/_react.default.createElement(_SvgChart.default, {
    margin: margin,
    svgRef: svgRef,
    chart: chart,
    tooltipRef: tooltipRef,
    palette: palette,
    filterComponent: filterComponent
  }, /*#__PURE__*/_react.default.createElement(_ScatterPlot.ScatterPlotGroup, {
    className: "scatter-plot"
  }, yMin < 0 && yMax > 0 && /*#__PURE__*/_react.default.createElement("line", {
    stroke: "lightgray",
    className: "x-zero",
    x1: xScale(Math.min(xMin, 0)),
    y1: yScale(0),
    x2: xScale(xMax) + 40,
    y2: yScale(0)
  }), xMin < 0 && xMax > 0 && /*#__PURE__*/_react.default.createElement("line", {
    stroke: "lightgray",
    className: "y-zero",
    x1: xScale(0),
    y1: yScale(yMin),
    x2: xScale(0),
    y2: yScale(yMax) - 40
  }), data.map(d => /*#__PURE__*/_react.default.createElement("g", {
    key: d.seriesKey
  }, d.points.map((p, idx) => /*#__PURE__*/_react.default.createElement(_react.Fragment, {
    key: "".concat(d.seriesKey, "-").concat(idx)
  }, /*#__PURE__*/_react.default.createElement("circle", {
    className: !animationDone ? "" : "full",
    cx: xScale(Number(p[xCol])),
    cy: yScale(Number(p[yCol])),
    r: 5,
    fill: colorPalette(d.seriesKey),
    fillOpacity: 0.8,
    onMouseEnter: e => showTooltip(e.target),
    onMouseLeave: () => hideTooltip(),
    "data-x": p[xCol],
    "data-series": d.seriesKey,
    "data-color": colorPalette(d.seriesKey)
  }), showLabels && /*#__PURE__*/_react.default.createElement("text", {
    x: xScale(Number(p[xCol])),
    y: yScale(Number(p[yCol])) + 12,
    textAnchor: "middle"
  }, seriesCol === _constants.COL_REL ? chart.dataView.columns[_constants.COL_GEO_ID].categories[p[_constants.COL_GEO_ID]].label : seriesCol === _constants.COL_DATE ? (0, _chartOperations.formatDate)(p[_constants.COL_DATE]) : chart.dataView.columns[seriesCol].categories[d.seriesKey].label)))))), /*#__PURE__*/_react.default.createElement("g", {
    className: "x-axis"
  }), /*#__PURE__*/_react.default.createElement("g", {
    className: "y-axis"
  }), /*#__PURE__*/_react.default.createElement(_ScatterPlot.AxisTitles, {
    className: "axis-titles"
  }, xAxisTitle && /*#__PURE__*/_react.default.createElement("text", {
    textAnchor: "start",
    x: width / 2,
    y: height + margin.top + 40,
    className: "x-axis-title"
  }, xAxisTitle), yAxisTitle && /*#__PURE__*/_react.default.createElement("text", {
    textAnchor: "middle",
    x: -margin.top - height / 2,
    y: -margin.left + 20,
    transform: "rotate(-90)",
    className: "y-axis-title"
  }, yAxisTitle)), /*#__PURE__*/_react.default.createElement(_ScatterPlot.ZoomButton, {
    x: 10,
    y: 10,
    fill: theme.colors.main,
    onClick: resetZoom,
    className: "reset-zoom"
  }, "Reset Zoom"));
});
var _default = ScatterPlot;
exports.default = _default;